/* ===========================================================================
   Source unique des données de financement (Alma / Younited).

   Consommée par la modale de location (section « Les autres solutions ») et,
   à terme, par le bloc financement de la fiche produit — pour qu'un seul
   endroit connaisse le markup des widgets tiers.

   Younited : window.younitedpay.rangeOffers expose des offres structurées
     (durée, mensualité, apport, frais, intérêts, total, TAEG). Rien à scraper.

   Alma : le widget replié n'affiche que « X € puis N x Y € ». Total dû, coût
     du crédit et TAEG ne vivent que dans la modale d'éligibilité, montée par
     react-modal à l'ouverture seulement. On l'ouvre donc masquée, on parcourt
     ses onglets, on lit les montants, puis on referme — une seule fois, le
     résultat étant mémorisé.

   API :
     GreenleazeFinancing.younited()  -> { available, offers: [Offer] }   (sync)
     GreenleazeFinancing.alma()      -> Promise<{ available, offers: [Offer] }>
     GreenleazeFinancing.clearCache()

   Offer (montants en euros, TAEG en %) :
     { provider, installments, monthly, downPayment, financed, cost, total, taeg }
   =========================================================================== */
(function () {
  'use strict';

  // Marqueur de version — pour vérifier en prod que cet asset est bien à jour :
  //   window.GreenleazeFinancing.version
  var VERSION = 'alma-schedule-2';

  // --- Sélecteurs Alma -------------------------------------------------------
  // Classes concaténées depuis « alma-payment-plans » / « alma-eligibility-modal »,
  // stables en v3.4.0 comme en v4.4.1. Détail utile : dans la modale les onglets
  // sont des <button> dans les deux majeures (contrairement au widget replié, où
  // v3 utilise des <div> survolés) — on peut donc cliquer sans distinction.
  var ALMA = {
    widget: '.alma-payment-plans-container',
    knowMore: '.alma-payment-plans-know-more-action', // v4 uniquement
    modal: '.alma-eligibility-modal-desktop-modal, .alma-eligibility-modal-mobile-modal',
    option: '.alma-eligibility-modal-eligibility-options button',
    activeOption: '.alma-eligibility-modal-active-option',
    details: '.alma-eligibility-modal-schedule-details',
    total: '.alma-eligibility-modal-schedule-total',
    credit: '.alma-eligibility-modal-schedule-credit',
    close: '.alma-eligibility-modal-close-button',
  };

  var CLOAK_ID = 'gl-financing-alma-cloak';
  var MODAL_TIMEOUT = 4000; // ms d'attente max du montage react-modal

  // --- Parsing --------------------------------------------------------------
  // « 2 450,52 € » -> 2450.52. `\s` couvre les insécables utilisées comme
  // séparateur de milliers par Intl.NumberFormat.
  var EURO_RE = /(\d[\d\s.,]*)\s*€/;
  var TAEG_RE = /TAEG\s*:?\s*([\d.,]+)\s*%/i;

  function parseFrNumber(raw) {
    if (raw == null) return null;
    // `\s` retire aussi les insécables (U+00A0 / U+202F) utilisées par Intl.
    // Le point est un séparateur de milliers en fr-FR, la virgule la décimale.
    var cleaned = String(raw).replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
    var n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  function euroIn(text) {
    if (!text) return null;
    var m = String(text).match(EURO_RE);
    return m ? parseFrNumber(m[1]) : null;
  }

  // Un élément dont TOUT le texte est un montant.
  var ONLY_EURO_RE = /^\d[\d\s.,]*\s*€$/;

  // Montant d'une ligne d'échéancier. La date et le montant vivent dans deux nœuds
  // distincts, mais leur concaténation dans textContent est ambiguë : « 29 août
  // 2026 » + « 247,20 € » donne « 2026 247,20 € », que rien ne distingue d'un
  // montant à 7 chiffres — les espaces sont des séparateurs de milliers valides en
  // fr-FR. On isole donc l'élément qui ne contient QUE le montant, sans présumer
  // de sa profondeur ni de sa position dans la ligne.
  function amountInRow(row) {
    var nodes = row.querySelectorAll('*');
    for (var i = nodes.length - 1; i >= 0; i--) {
      var text = nodes[i].textContent.trim();
      if (ONLY_EURO_RE.test(text)) return parseFrNumber(text.replace('€', '').trim());
    }
    var own = row.textContent.trim();
    if (ONLY_EURO_RE.test(own)) return parseFrNumber(own.replace('€', '').trim());
    return euroIn(own); // dernier recours : structure inattendue
  }

  // 🔴 NE PAS passer par parseFrNumber ici. Celui-ci retire TOUS les points, traités
  // comme séparateurs de milliers (juste pour un montant en fr-FR : « 3.399,00 »).
  // Dans un TAEG, le point ne peut être qu'un séparateur DÉCIMAL — un TAEG est borné
  // par la loi bien en dessous de 1 000, il n'a jamais de milliers. Un « 19.90 % »
  // servi par Alma devenait donc 1990 %, affiché tel quel dans la mention légale de la
  // modale et dans la ligne du widget de la fiche.
  // Alma formate normalement en fr-FR (virgule), mais son bundle flotte en
  // `@alma/widgets@3.x.x` et le format dépend de la locale négociée : on accepte les
  // deux séparateurs plutôt que de parier sur l'un.
  function taegIn(text) {
    if (!text) return null;
    var m = String(text).match(TAEG_RE);
    if (!m) return null;
    var n = parseFloat(String(m[1]).replace(/\s/g, '').replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  function textOf(root, selector) {
    if (!root) return null;
    var el = root.querySelector(selector);
    return el ? el.textContent.trim() : null;
  }

  function toNumber(v) {
    if (v == null || v === '') return null;
    var n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  // ==========================================================================
  // Younited — données structurées, aucune modale
  // ==========================================================================

  // Younited publie TOUTE sa plage de maturités (souvent 10 à 84 mois d'affilée) :
  // c'est sa fonctionnalité « range », pensée pour un curseur. Transposées en
  // boutons, ces 75 options débordent et noient le choix. On garde donc une échelle
  // de paliers ronds couvrant toute la plage — 84 mois compris, qui porte la
  // mensualité la plus basse et n'a aucune raison d'être amputé.
  var YOUNITED_LADDER = [24, 36, 60, 84];
  var YOUNITED_FALLBACK_COUNT = 5;

  // Plancher produit : les durées courtes ne sont pas proposées (mensualité trop
  // élevée pour être un argument). Appliqué avant la curation, donc quel que soit
  // le chemin — échelle de paliers comme échantillonnage de repli.
  var YOUNITED_MIN_INSTALLMENTS = 24;

  function curateOffers(offers) {
    if (offers.length <= YOUNITED_FALLBACK_COUNT) return offers;

    var kept = offers.filter(function (o) {
      return YOUNITED_LADDER.indexOf(o.installments) !== -1;
    });

    // La durée la plus longue disponible reste proposée même hors paliers : c'est
    // elle qui donne la mensualité d'appel affichée sur la carte.
    var longest = offers[offers.length - 1];
    if (kept.length && kept.indexOf(longest) === -1) kept.push(longest);
    if (kept.length >= 2) return kept;

    // Aucun palier disponible : échantillon régulier, bornes comprises.
    var step = (offers.length - 1) / (YOUNITED_FALLBACK_COUNT - 1);
    var sample = [];
    for (var i = 0; i < YOUNITED_FALLBACK_COUNT; i++) {
      var offer = offers[Math.round(i * step)];
      if (offer && sample.indexOf(offer) === -1) sample.push(offer);
    }
    return sample;
  }

  function younited() {
    var yp = window.younitedpay;
    var raw = yp && yp.rangeOffers;
    if (!Array.isArray(raw) || !raw.length) return { available: false, offers: [] };

    var offers = raw
      .map(function (o) {
        var fees = toNumber(o.fee_total) || 0;
        var interest = toNumber(o.interest_total) || 0;
        return {
          provider: 'younited',
          installments: toNumber(o.maturity),
          monthly: toNumber(o.installment_amount),
          firstAmount: toNumber(o.installment_amount),
          downPayment: toNumber(o.down_payment_amount),
          financed: toNumber(o.initial_amount),
          cost: fees + interest,
          total: toNumber(o.total_amount),
          taeg: toNumber(o.taeg),
          type: o.type || null,
        };
      })
      .filter(function (o) {
        return o.installments >= YOUNITED_MIN_INSTALLMENTS;
      })
      .sort(function (a, b) {
        return a.installments - b.installments;
      });

    var curated = curateOffers(offers);
    return { available: curated.length > 0, offers: curated };
  }

  // ==========================================================================
  // Alma — ouverture masquée de la modale d'éligibilité
  // ==========================================================================

  // Masque la modale d'Alma le temps de la lecture. opacity + pointer-events
  // plutôt que display:none : le contenu doit rester rendu pour que React le peuple.
  //
  // On ne peut pas cibler l'overlay par sa classe : Alma passe un
  // `overlayClassName` haché par son bundler (« _1yxCb » en v3.4.0), qui remplace
  // le `ReactModal__Overlay` par défaut et change à chaque build. On masque donc
  // les nœuds que react-modal ajoute au <body> au moment où il les y ajoute — le
  // callback d'un MutationObserver s'exécute en microtâche, donc avant le rendu.
  var cloakObserver = null;
  var cloakedNodes = [];

  function isCloaked(node) {
    for (var i = 0; i < cloakedNodes.length; i++) {
      if (cloakedNodes[i].node === node) return true;
    }
    return false;
  }

  function cloakNode(node) {
    if (node.nodeType !== 1 || isCloaked(node)) return;
    cloakedNodes.push({ node: node, style: node.getAttribute('style') });
    node.style.setProperty('opacity', '0', 'important');
    node.style.setProperty('pointer-events', 'none', 'important');
    node.style.setProperty('transition', 'none', 'important');
  }

  function cloak(on) {
    var existing = document.getElementById(CLOAK_ID);

    if (!on) {
      if (cloakObserver) cloakObserver.disconnect();
      cloakObserver = null;
      cloakedNodes.forEach(function (entry) {
        if (entry.style == null) entry.node.removeAttribute('style');
        else entry.node.setAttribute('style', entry.style);
      });
      cloakedNodes = [];
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }

    if (cloakObserver) return;

    // Filet complémentaire pour les conteneurs dont la classe, elle, est stable.
    if (!existing) {
      var style = document.createElement('style');
      style.id = CLOAK_ID;
      style.textContent = ALMA.modal + '{opacity:0!important;pointer-events:none!important}';
      document.head.appendChild(style);
    }

    cloakObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, cloakNode);
      });
    });
    cloakObserver.observe(document.body, { childList: true });
  }

  function almaTrigger() {
    return document.querySelector(ALMA.knowMore) || document.querySelector(ALMA.widget);
  }

  // Attend l'apparition d'un nœud correspondant au sélecteur, sous observation
  // de <body> (react-modal monte en portail hors du widget).
  function waitFor(selector, timeout) {
    return new Promise(function (resolve) {
      var found = document.querySelector(selector);
      if (found) return resolve(found);
      var done = false;
      var observer = new MutationObserver(function () {
        var el = document.querySelector(selector);
        if (!el || done) return;
        done = true;
        observer.disconnect();
        window.clearTimeout(timer);
        resolve(el);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        observer.disconnect();
        resolve(null);
      }, timeout || MODAL_TIMEOUT);
    });
  }

  function nextFrame() {
    return new Promise(function (resolve) {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          window.setTimeout(resolve, 0);
        });
      } else {
        window.setTimeout(resolve, 16);
      }
    });
  }

  // Lit l'onglet actif de la modale : échéancier détaillé, total dû, coût du crédit,
  // TAEG. L'échéancier (une ligne par échéance, montant formaté) donne à la fois le
  // nombre d'échéances et les montants — plus fiable que de parser le libellé d'onglet.
  function readActivePlan(modal) {
    var creditTxt = textOf(modal, ALMA.credit);
    var total = euroIn(textOf(modal, ALMA.total));
    var cost = euroIn(creditTxt);

    var details = modal.querySelector(ALMA.details);
    var amounts = details
      ? Array.prototype.slice
          .call(details.children)
          .map(amountInRow)
          .filter(function (v) {
            return v != null;
          })
      : [];

    // Repli sur le libellé d'onglet si l'échéancier n'est pas lisible.
    var installments = amounts.length;
    if (!installments) {
      var active = modal.querySelector(ALMA.activeOption);
      var m = (active ? active.textContent : '').match(/(\d+)\s*[x×]/i);
      installments = m ? parseInt(m[1], 10) : null;
    }

    return {
      provider: 'alma',
      installments: installments,
      // La 1re échéance est prélevée aujourd'hui et diffère souvent des suivantes
      // (elle porte les frais) : la « mensualité » est donc la 2e ligne.
      firstAmount: amounts.length ? amounts[0] : null,
      monthly: amounts.length > 1 ? amounts[1] : amounts.length === 1 ? amounts[0] : null,
      downPayment: null, // Alma ne propose pas d'apport paramétrable
      financed: total != null && cost != null ? Math.round((total - cost) * 100) / 100 : null,
      cost: cost,
      total: total,
      taeg: taegIn(creditTxt),
    };
  }

  // Le bouton de fermeture est rendu par react-modal EN DEHORS de
  // .alma-eligibility-modal-desktop-modal (composant frère) : on le cherche donc
  // dans tout le document, pas dans le nœud de la modale.
  function closeAlma(modal) {
    var btn = document.querySelector(ALMA.close) || (modal && modal.querySelector(ALMA.close));
    if (btn) {
      btn.click();
      return;
    }
    // Dernier recours. `scraping` reste vrai pendant la propagation : la modale de
    // location s'en sert pour ignorer cet Escape, qui la fermerait sinon.
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }

  var almaPromise = null;
  var scraping = false;

  function almaScrape() {
    var trigger = almaTrigger();
    if (!trigger) return Promise.resolve({ available: false, offers: [] });

    // react-modal verrouille le scroll (overflow:hidden) et le relâche à la
    // fermeture — ce qui déverrouillerait la page derrière notre propre modale.
    // On mémorise l'état pour le réappliquer après coup.
    var bodyOverflow = document.body.style.overflow;

    // Ce même verrou passe <html> en position:fixed et se relâche à la fermeture par
    // un window.scroll(0, y) synchrone. Or le thème pose `scroll-behavior: smooth`
    // sur <html> : la remise en place est alors ANIMÉE depuis le haut de page
    // (mesuré le 2026-09-04 : 0 → 800 px en ~550 ms à chaque lecture au chargement,
    // vécu comme un scroll qui « remonte puis redescend »). Neutralisé le temps de
    // la lecture — même parade que unlockScroll (modale de location).
    var html = document.documentElement;
    var htmlScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    scraping = true;
    cloak(true);
    trigger.click();

    return waitFor(ALMA.modal, MODAL_TIMEOUT)
      .then(function (modal) {
        if (!modal) return { available: false, offers: [] };

        var buttons = Array.prototype.slice.call(modal.querySelectorAll(ALMA.option));
        // Pas d'onglets détectés : on lit au moins le plan affiché.
        if (!buttons.length) {
          var single = readActivePlan(modal);
          closeAlma(modal);
          return { available: single.total != null, offers: single.total != null ? [single] : [] };
        }

        // Une seule ouverture pour toutes les durées : on parcourt les onglets.
        var offers = [];
        var chain = Promise.resolve();
        buttons.forEach(function (btn) {
          chain = chain
            .then(function () {
              btn.click();
              return nextFrame();
            })
            .then(function () {
              var plan = readActivePlan(modal);
              if (plan.total != null) offers.push(plan);
            });
        });

        return chain.then(function () {
          closeAlma(modal);
          offers.sort(function (a, b) {
            return (a.installments || 0) - (b.installments || 0);
          });
          return { available: offers.length > 0, offers: offers };
        });
      })
      .catch(function (e) {
        console.error('[financing] lecture Alma échouée', e);
        return { available: false, offers: [] };
      })
      .then(function (result) {
        cloak(false);
        // Après le window.scroll de fermeture d'Alma (synchrone dans le clic Fermer,
        // donc toujours passé ici) : on rend son animation de scroll à la page.
        html.style.scrollBehavior = htmlScrollBehavior;
        // Restaure le verrou de scroll tel qu'il était avant l'ouverture d'Alma.
        document.body.style.overflow = bodyOverflow;
        // Relâché en fin de tâche : l'Escape de repli peut encore se propager.
        window.setTimeout(function () {
          scraping = false;
        }, 0);
        return result;
      });
  }

  function alma() {
    if (!almaPromise) {
      // Rien à mémoriser tant que le widget n'est pas dans le DOM : l'app Alma est
      // chargée en async, et un appel arrivé avant son rendu figerait `almaPromise` sur
      // « indisponible » pour TOUTE la page — Alma n'aurait plus jamais ses offres, même
      // une fois le widget rendu. On renvoie donc une réponse jetable, et le consommateur
      // (watchOtherSolutions, côté modale de location) rappellera.
      if (!almaTrigger()) return Promise.resolve({ available: false, offers: [] });
      almaPromise = almaScrape();
    }
    return almaPromise;
  }

  // Lecture immédiate, sans ouvrir la modale : le widget replié annonce
  // « X € puis N x Y € ». Suffit à peupler la carte (mensualité) avant tout clic ;
  // le récap détaillé, lui, exige alma().
  var INLINE_RE = /(\d[\d\s.,]*)\s*€\s*puis\s*(\d+)\s*x\s*(\d[\d\s.,]*)\s*€/i;

  function almaInline() {
    var el = document.querySelector('.alma-payment-plans-payment-info, #payment-info-text');
    var text = el ? el.textContent.trim() : null;
    var m = text ? text.match(INLINE_RE) : null;
    if (!m) return null;
    var remaining = parseInt(m[2], 10);
    return {
      provider: 'alma',
      installments: remaining + 1, // N échéances restantes + celle d'aujourd'hui
      firstAmount: parseFrNumber(m[1]),
      monthly: parseFrNumber(m[3]),
    };
  }

  function clearCache() {
    almaPromise = null;
  }

  // --- Logos officiels -------------------------------------------------------
  // Les widgets embarquent le logo de leur marque : on le clone plutôt que de le
  // reproduire typographiquement. Zéro asset à héberger, zéro question de charte,
  // et le logo suit automatiquement les évolutions de l'app.
  // Alma le rend en SVG inline (data-testid="Alma-Logo" en v4, aria-hidden en v3),
  // Younited en <img class="yp-logo-widget">.
  // Renvoie un nœud DÉTACHÉ prêt à insérer, ou null si le widget n'a pas encore rendu.
  function logo(provider) {
    if (provider === 'alma') {
      var root = document.querySelector('.alma_widget_block') || document.querySelector(ALMA.widget);
      var svg =
        root &&
        (root.querySelector('svg[data-testid="Alma-Logo"]') ||
          root.querySelector('svg[aria-hidden="true"]') ||
          root.querySelector('svg'));
      if (!svg) return null;

      var clone = svg.cloneNode(true);
      // Le contrat est « un nœud PRÊT À INSÉRER ». Or la source peut être masquée par un
      // autre consommateur — le widget de la buybox escamote le logo d'origine au profit
      // de sa propre pastille — et cloneNode copie les styles inline. Sans ce nettoyage,
      // la carte Alma de la modale héritait du masquage et restait vide.
      clone.style.removeProperty('display');
      clone.style.removeProperty('visibility');
      return clone;
    }

    var img = document.querySelector('.yp-logo-widget');
    var src = img && img.getAttribute('src');
    if (!src) return null;
    // On reconstruit l'<img> au lieu de la cloner : le nœud d'origine peut porter
    // des attributs de dimensionnement propres au widget.
    var clone = document.createElement('img');
    clone.setAttribute('src', src);
    clone.setAttribute('alt', '');
    return clone;
  }

  window.GreenleazeFinancing = {
    version: VERSION,
    younited: younited,
    alma: alma,
    almaInline: almaInline,
    clearCache: clearCache,
    logo: logo,
    // Vrai pendant l'ouverture masquée de la modale Alma. La modale de location
    // le consulte pour ne pas se fermer sur l'Escape que cette lecture peut émettre.
    isScraping: function () {
      return scraping;
    },
    // Exposés pour les tests et les consommateurs qui formatent eux-mêmes.
    _parse: { euroIn: euroIn, taegIn: taegIn, parseFrNumber: parseFrNumber, amountInRow: amountInRow },
  };
})();
